import React, { useEffect, useMemo, useState } from 'react'
import ReactApexChart from 'react-apexcharts'
import { Spinner, Alert } from 'reactstrap'
import { mmYyyyFromMesRef } from '../../../utils/monthSelectOptions'
import { fetchRhGraficos } from './rhDashboardApi'
import '../../Financial/dashboard.scss'

const CHART_COLORS = {
  atividade: '#5664d2',
  retroativas: '#f1b44c',
}

const MAX_ITENS = 20

const segundosParaHoras = (segundos) => Number(((segundos || 0) / 3600).toFixed(2))

const RhVisaoGeralChart = ({ mesRef, dimensao, metrica, colaboradoresFiltrados }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await fetchRhGraficos(mmYyyyFromMesRef(mesRef))
        if (!cancelled) {
          if (res?.ok === false) {
            setData(null)
            setError(res.msg || 'Erro ao carregar gráficos.')
          } else {
            setData(res)
          }
        }
      } catch (err) {
        if (!cancelled) {
          setData(null)
          setError(String(err))
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [mesRef])

  const chartItems = useMemo(() => {
    if (!data) return []
    const metricKey = metrica === 'retroativas' ? 'horas_retroativas_s' : 'horas_atividade_s'
    const fmtKey = metrica === 'retroativas' ? 'horas_retroativas_fmt' : 'horas_atividade_fmt'

    let items = []
    if (dimensao === 'projeto') {
      items = (data.por_projeto || []).map(item => ({
        label: item.projeto,
        segundos: item[metricKey] || 0,
        fmt: item[fmtKey] || '0h00min',
      }))
    } else {
      const deptPfIds = new Set((colaboradoresFiltrados || []).map(c => c.pf_id))
      items = (data.por_colaborador || [])
        .filter(item => deptPfIds.size === 0 || deptPfIds.has(item.pf_id))
        .map(item => ({
          label: item.nome,
          segundos: item[metricKey] || 0,
          fmt: item[fmtKey] || '0h00min',
        }))
    }

    return items
      .filter(item => item.segundos > 0)
      .sort((a, b) => b.segundos - a.segundos)
      .slice(0, MAX_ITENS)
  }, [data, dimensao, metrica, colaboradoresFiltrados])

  if (loading) {
    return <div className="text-center py-5"><Spinner color="primary" /></div>
  }
  if (error) {
    return <Alert color="danger" className="mb-0">{error}</Alert>
  }
  if (!chartItems.length) {
    return <p className="text-muted mb-0">Nenhum dado para exibir no gráfico.</p>
  }

  const categories = chartItems.map(i => i.label)
  const seriesData = chartItems.map(i => segundosParaHoras(i.segundos))
  const seriesName = metrica === 'retroativas' ? 'Horas retroativas' : 'Horas em atividade'
  const chartHeight = Math.max(280, chartItems.length * 36 + 80)

  return (
    <>
      <div id="rh-visao-geral-chart" className="apex-charts" dir="ltr">
        <ReactApexChart
          type="bar"
          height={chartHeight}
          series={[{ name: seriesName, data: seriesData }]}
          options={{
            chart: {
              type: 'bar',
              toolbar: { show: false },
              fontFamily: 'inherit',
            },
            plotOptions: {
              bar: {
                horizontal: true,
                barHeight: '65%',
              },
            },
            stroke: { width: 0 },
            dataLabels: { enabled: false },
            colors: [CHART_COLORS[metrica] || CHART_COLORS.atividade],
            xaxis: {
              categories,
              title: { text: 'Horas', style: { fontSize: '12px' } },
              labels: {
                formatter: (v) => Number(v).toFixed(1),
                style: { fontSize: '11px' },
              },
            },
            yaxis: {
              labels: {
                style: { fontSize: '11px' },
                maxWidth: 220,
              },
            },
            tooltip: {
              x: {
                formatter: (val, opts) => chartItems[opts?.dataPointIndex]?.fmt || `${val} h`,
              },
            },
            legend: { show: false },
            grid: {
              borderColor: '#f1f3f4',
              strokeDashArray: 3,
            },
          }}
        />
      </div>
      {chartItems.length >= MAX_ITENS && (
        <div className="text-muted small mt-2">
          Exibindo os
          {' '}
          {MAX_ITENS}
          {' '}
          primeiros por volume de horas.
        </div>
      )}
    </>
  )
}

export default RhVisaoGeralChart
